/**
 * Shared markdown rendering for artifacts.
 *
 * Extracted from `pages/Artifacts.tsx` so the artifact detail page and the
 * in-conversation artifact viewer render identically — a support request shows a
 * preview of the same document the artifact page shows in full, and the two
 * drifting apart would be worse than the duplication.
 *
 * Includes the mermaid code-block renderer, since artifacts routinely contain
 * diagrams and a preview that silently dropped them would misrepresent the doc.
 */
import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import remarkGfm from 'remark-gfm';
import { MermaidDiagram } from './MermaidDiagram';
import elliePng from '@/assets/images/pliable/ellie.png';

/** Recursively extract text content from React children */
function extractText(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (!children) return '';
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (typeof children === 'object' && 'props' in children) {
    return extractText((children as any).props.children);
  }
  return '';
}

/** Strip the "Ellie:" or "**Ellie:**" prefix from blockquote children */
function stripElliePrefix(children: ReactNode): ReactNode {
  if (!children) return children;

  // children is typically an array of <p> elements from markdown
  const childArray = Array.isArray(children) ? children : [children];

  return childArray.map((child: any, i: number) => {
    if (i !== 0) return child;
    if (!child?.props?.children) return child;

    const grandChildren = Array.isArray(child.props.children)
      ? child.props.children
      : [child.props.children];

    // Find and strip the "Ellie:" prefix which may be in a <strong> or plain text
    const stripped = grandChildren.flatMap((gc: any, j: number) => {
      if (j > 0) return [gc];

      // Case: <strong>Ellie:</strong> (from **Ellie:**)
      if (gc?.props?.children) {
        const strongText = extractText(gc.props.children);
        if (strongText.trim() === 'Ellie:') return [];
        if (strongText.trim().startsWith('Ellie:')) {
          return [{ ...gc, props: { ...gc.props, children: strongText.replace(/^Ellie:\s*/, '') } }];
        }
      }

      // Case: plain text "Ellie: ..."
      if (typeof gc === 'string' && gc.trimStart().startsWith('Ellie:')) {
        const rest = gc.replace(/^\s*Ellie:\s*/, '');
        return rest ? [rest] : [];
      }

      return [gc];
    });

    // Filter out leading whitespace-only strings
    const trimmed = stripped.length > 0 && typeof stripped[0] === 'string' && stripped[0].trim() === ''
      ? stripped.slice(1)
      : stripped;

    return { ...child, props: { ...child.props, children: trimmed } };
  });
}

export const remarkPlugins = [remarkGfm];

export const markdownComponents = {
  h1: ({ className, ...props }: any) => (
    <h1 className={cn('text-2xl font-bold mb-4 first:mt-0 mt-6', className)} {...props} />
  ),
  h2: ({ className, ...props }: any) => (
    <h2 className={cn('text-xl font-bold mb-3 first:mt-0 mt-5', className)} {...props} />
  ),
  h3: ({ className, ...props }: any) => (
    <h3 className={cn('text-lg font-semibold mb-2 first:mt-0 mt-4', className)} {...props} />
  ),
  h4: ({ className, ...props }: any) => (
    <h4 className={cn('text-base font-semibold mb-2 first:mt-0 mt-3', className)} {...props} />
  ),
  p: ({ className, ...props }: any) => (
    <p className={cn('mb-4 last:mb-0', className)} {...props} />
  ),
  a: ({ className, ...props }: any) => (
    <a className={cn('text-primary underline hover:text-primary/80', className)} {...props} />
  ),
  blockquote: ({ children, className, ...props }: any) => {
    // Check if the blockquote starts with "Ellie:" by inspecting text content
    const textContent = extractText(children);
    const isEllie = textContent.trimStart().startsWith('Ellie:');

    if (isEllie) {
      return (
        <div className="my-6 rounded-xl border border-border bg-card/50 p-5 pl-6 border-l-4 border-l-primary">
          <div className="flex items-center gap-2.5 mb-3">
            <img src={elliePng} alt="Ellie" className="w-7 h-7 rounded-full object-cover" />
            <div>
              <div className="text-sm font-semibold leading-none">Ellie</div>
              <div className="text-xs text-muted-foreground">Pliable AI Analyst</div>
            </div>
          </div>
          <div className="text-sm text-muted-foreground [&>p]:mb-2 [&>p:last-child]:mb-0">
            {stripElliePrefix(children)}
          </div>
        </div>
      );
    }

    return (
      <blockquote className={cn('border-l-4 border-border pl-4 italic my-4', className)} {...props}>
        {children}
      </blockquote>
    );
  },
  ul: ({ className, ...props }: any) => (
    <ul className={cn('list-disc mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  ol: ({ className, ...props }: any) => (
    <ol className={cn('list-decimal mb-4 space-y-1 pl-6', className)} {...props} />
  ),
  li: ({ className, ...props }: any) => (
    <li className={cn('pl-2', className)} {...props} />
  ),
  hr: ({ className, ...props }: any) => (
    <hr className={cn('my-4 border-border', className)} {...props} />
  ),
  table: ({ className, ...props }: any) => (
    <table className={cn('w-full border-collapse border border-border my-4', className)} {...props} />
  ),
  th: ({ className, ...props }: any) => (
    <th className={cn('border border-border px-4 py-2 bg-muted font-semibold text-left', className)} {...props} />
  ),
  td: ({ className, ...props }: any) => (
    <td className={cn('border border-border px-4 py-2', className)} {...props} />
  ),
  tr: ({ className, ...props }: any) => (
    <tr className={cn('', className)} {...props} />
  ),
  code: ({ className, children, ...props }: any) => {
    // Check if this is a mermaid code block (inside a <pre>)
    const match = /language-mermaid/.exec(className || '');
    if (match) {
      return <MermaidDiagram code={String(children).replace(/\n$/, '')} />;
    }
    return (
      <code className={cn('bg-muted px-1.5 py-0.5 rounded text-sm font-mono', className)} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: any) => {
    // If the child is a mermaid code block, render without the <pre> wrapper
    const child = Array.isArray(children) ? children[0] : children;
    if (child?.props?.className?.includes('language-mermaid')) {
      return <>{children}</>;
    }
    return (
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto my-4 [&>code]:block [&>code]:p-0 [&>code]:bg-transparent" {...props}>
        {children}
      </pre>
    );
  },
};
